import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChannelIcon } from '@/core/hooks/useChannelIcon';
import { useValidatedThumbnail } from '@/core/hooks/useValidatedThumbnail';
import { Post } from '@/core/services/interfaces/PostInterfaces';
import { MOCK_POSTS } from '@/core/services/interfaces/PostMockData';
import { CLASSIFICATION_EVENTS } from '../constants/events';
import { FullClassifyModal } from './FullClassifyModal';
import { useMultipleEdition } from '../contexts/MultipleEditionContext';

import { LeftPanel } from './ClassificationCard/components/LeftPanel';
import { PostArea } from './ClassificationCard/components/PostArea';
import { ClassificationSection } from './ClassificationCard/components/ClassificationSection';
import { ClassificationItemsSection } from './ClassificationCard/components/ClassificationItemsSection';
import { Footer } from './ClassificationCard/components/Footer';

import {
  Gender,
  Sentiment,
  parseGender,
  parseSentiment,
  resolveImageUrl,
} from './ClassificationCard/types';

import './ClassificationCard.scss';

interface ClassificationCardProps {
  post?: Post;
}

function applyStatIfChanged<T>(value: T | undefined, setter: React.Dispatch<React.SetStateAction<T>>): void {
  if (value !== undefined) setter((prev) => (value === prev ? prev : value));
}

// ── Mirrors PostCard's parseChecked ─────────────────────────────
function parseChecked(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'sim'].includes(normalized)) return true;
    if (['false', '0', 'no', 'nao', 'não', ''].includes(normalized)) return false;
  }
  return Boolean(value);
}

const DEFAULT_POST = process.env.NODE_ENV === 'development' ? MOCK_POSTS[1] : undefined;

export const ClassificationCard: React.FC<ClassificationCardProps> = ({ post = DEFAULT_POST }) => {
  const LINE_HEIGHT_PX = 20;
  const MAX_LINES = 9;
  const GRADIENT_THRESHOLD_LINES = 9;
  const { t } = useTranslation();
  const platform = (post?.primary_channel ?? post?.channel ?? '').toLowerCase();
  const textRef = useRef<HTMLParagraphElement>(null);
  const [hasGradient, setHasGradient] = useState(false);
  const [gender, setGender] = useState<Gender | null>(parseGender(post?.gender));
  const [sentiment, setSentiment] = useState<Sentiment | null>(parseSentiment(post?.polarity));
  const [showClassifyModal, setShowClassifyModal] = useState(false);
  const [isRemoved, setIsRemoved] = useState(false);
  const [isSpam, setIsSpam] = useState<boolean>(post?.spam ?? false);

  // ── Initialize isChecked from post data, just like PostCard ──
  const [isChecked, setIsChecked] = useState<boolean>(
    parseChecked((post as any)?.favorite ?? (post as any)?.checked),
  );

  const [metricsUpdatedAt, setMetricsUpdatedAt] = useState(post?.metrics_updated_at ?? '');
  const [likes, setLikes] = useState<number | null>(post?.likes ?? null);
  const [shares, setShares] = useState<number | null>(post?.shares ?? null);
  const [comments, setComments] = useState<number | null>(post?.comments ?? null);
  const [followers, setFollowers] = useState<number | null>(post?.followers ?? null);

  const channelData = useChannelIcon(platform);
  const avatarThumbnail = useValidatedThumbnail(post?.post_user_image_url);
  const imageUrl = post ? resolveImageUrl(post) : undefined;
  const {
    isMultipleEditionOpen,
    selectedPostIds,
    togglePostSelection,
    availableGroups,
    availableThemes,
    availableInterests,
    availableTags,
    loadingAvailableItems,
    addTagsLocally,
    refetchAvailableTags,
  } = useMultipleEdition();
  const postId = post?.pid ?? post?._id;
  const isSelected = !!postId && selectedPostIds.has(postId);

  // ── Gradient detection ──────────────────────────────────────
  useEffect(() => {
    if (textRef.current) {
      const overflows =
        textRef.current.scrollHeight > LINE_HEIGHT_PX * GRADIENT_THRESHOLD_LINES;
      setHasGradient(overflows);
    }
  }, []);

  // ── Warroom event bus ───────────────────────────────────────
  useEffect(() => {
    const handleInitState = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      if (detail.postId !== post?.pid) return;
      if (detail.metricsUpdatedAt !== undefined) setMetricsUpdatedAt(detail.metricsUpdatedAt);
      if (detail.polarity !== undefined) setSentiment(parseSentiment(detail.polarity));
      if (detail.gender !== undefined) setGender(parseGender(detail.gender));
      // ── NEW: sync checked state from warroom ──────────────
      if (detail.checked !== undefined) setIsChecked(parseChecked(detail.checked));
      if (detail.spam !== undefined) setIsSpam(Boolean(detail.spam));
    };

    const handleRemove = (e: Event) => {
      if ((e as CustomEvent).detail?.postId === post?.pid) setIsRemoved(true);
    };

    const handleRefreshInteractions = (e: Event) => {
      if ((e as CustomEvent).detail?.postId !== post?.pid) return;
      const updatedPost: Partial<Post> = (e as CustomEvent).detail?.post ?? {};
      if (!updatedPost || updatedPost.pid !== post?.pid) return;
      applyStatIfChanged<number | null>(updatedPost.likes, setLikes);
      applyStatIfChanged<number | null>(updatedPost.shares, setShares);
      applyStatIfChanged<number | null>(updatedPost.comments, setComments);
      applyStatIfChanged<number | null>(updatedPost.followers, setFollowers);
      applyStatIfChanged(updatedPost.metrics_updated_at, setMetricsUpdatedAt);
    };

    document.addEventListener(CLASSIFICATION_EVENTS.REFRESH_POST_INTERACTIONS, handleRefreshInteractions);
    document.addEventListener(CLASSIFICATION_EVENTS.INIT_POST_STATE, handleInitState);
    document.addEventListener(CLASSIFICATION_EVENTS.REMOVE_POST, handleRemove);

    document.dispatchEvent(
      new CustomEvent(CLASSIFICATION_EVENTS.CARD_READY, {
        detail: { postId: post?.pid },
        bubbles: true,
        composed: true,
      }),
    );

    return () => {
      document.removeEventListener(CLASSIFICATION_EVENTS.REFRESH_POST_INTERACTIONS, handleRefreshInteractions);
      document.removeEventListener(CLASSIFICATION_EVENTS.INIT_POST_STATE, handleInitState);
      document.removeEventListener(CLASSIFICATION_EVENTS.REMOVE_POST, handleRemove);
    };
  }, [post?.pid]);

  // ── Bulk apply: immediate local update ─────────────────────
  useEffect(() => {
    const handleBulkApply = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      const currentPostId = post?.pid ?? post?._id;
      if (detail.postId !== currentPostId) return;
      if (detail.sentiment != null) setSentiment(detail.sentiment);
      if (detail.gender != null) setGender(detail.gender);
      if (detail.checkedActive) setIsChecked(true);
      if (detail.removeActive) setIsRemoved(true);
      if (detail.spam !== undefined) setIsSpam(detail.spam);
    };

    document.addEventListener(CLASSIFICATION_EVENTS.BULK_APPLY_LOCAL, handleBulkApply);
    return () => document.removeEventListener(CLASSIFICATION_EVENTS.BULK_APPLY_LOCAL, handleBulkApply);
  }, [post?.pid, post?._id]);

  if (!post) return null;
  if (isRemoved) return null;

  // ── Event dispatch helpers ──────────────────────────────────
  const dispatch = (
    eventName: string,
    extra?: Record<string, unknown>,
  ) => {
    document.dispatchEvent(
      new CustomEvent(eventName, {
        detail: {
          postId: post.pid ?? post._id,
          channel: post.channel ?? post.primary_channel ?? '',
          uid: post.uid ?? '',
          ...(post.post_id && { post_id: post.post_id }),
          ...(post.sac_call_id && { sac_call_id: post.sac_call_id }),
          ...extra,
        },
        bubbles: true,
        composed: true,
      }),
    );
  };

  const handleGenderChange = (option: Gender) => {
    // ── Clicking the already-active gender un-classifies it (0) ──
    const newGender = gender === option ? null : option;
    setGender(newGender);
    if (!isMultipleEditionOpen || !isSelected) {
      dispatch(CLASSIFICATION_EVENTS.GENDER_CHANGE, { gender: newGender ?? 0 });
    }
  };

  const handleSentimentChange = (option: Sentiment) => {
    // ── Clicking the already-active sentiment un-classifies it (polarity 0) ──
    const newSentiment = sentiment === option ? null : option;
    setSentiment(newSentiment);
    if (!isMultipleEditionOpen || !isSelected) {
      dispatch(CLASSIFICATION_EVENTS.SENTIMENT_CHANGE, { sentiment: newSentiment ?? 0 });
    }
  };

  const GENDER_LABELS: Record<Gender, string> = {
    man: t('classificationCard.genderMale'),
    woman: t('classificationCard.genderFemale'),
    company: t('classificationCard.genderOrganization'),
  };

  const SENTIMENT_LABELS: Record<Exclude<Sentiment, null>, string> = {
    Negativo: t('classificationCard.sentimentNegative'),
    Neutro: t('classificationCard.sentimentNeutral'),
    Positivo: t('classificationCard.sentimentPositive'),
  };

  return (
    <>
      <article className="classification-card">
        {isMultipleEditionOpen && postId && (
          <button
            className={`classification-card__selection-overlay${selectedPostIds.has(postId) ? ' classification-card__selection-overlay--selected' : ''}`}
            onClick={() => togglePostSelection(postId)}
            aria-pressed={selectedPostIds.has(postId)}
            aria-label={selectedPostIds.has(postId) ? t('multipleEdition.cardSelectedLabel') : t('multipleEdition.cardSelectLabel')}
          >
            <span className="classification-card__selection-label">
              {selectedPostIds.has(postId)
                ? t('multipleEdition.cardSelectedLabel')
                : t('multipleEdition.cardSelectLabel')}
            </span>
          </button>
        )}
        {/* Left panel */}
        <LeftPanel
          post={post}
          avatarThumbnail={avatarThumbnail}
          channelData={channelData}
          platform={platform}
          metricsUpdatedAt={metricsUpdatedAt}
          likes={likes}
          shares={shares}
          comments={comments}
          followers={followers}
          onDispatch={dispatch}
          CLASSIFICATION_EVENTS={CLASSIFICATION_EVENTS}
        />

        <div className="classification-card__vdivider" />

        <section className="classification-card__right">
          {/* Post content area */}
          <PostArea
            post={post}
            textRef={textRef}
            hasGradient={hasGradient}
            imageUrl={imageUrl}
            isChecked={isChecked}
            platform={platform}
            onDispatch={dispatch}
            onSetIsChecked={setIsChecked}
            onSetShowClassifyModal={setShowClassifyModal}
            CLASSIFICATION_EVENTS={CLASSIFICATION_EVENTS}
            lineHeight={LINE_HEIGHT_PX}
            maxLines={MAX_LINES}
          />

          <hr className="classification-card__divider" />

          {/* Classification section */}
          <ClassificationSection
            gender={gender}
            sentiment={sentiment}
            onGenderChange={handleGenderChange}
            onSentimentChange={handleSentimentChange}
            GENDER_LABELS={GENDER_LABELS}
            SENTIMENT_LABELS={SENTIMENT_LABELS}
          />

          {/* Classification items rows (groups, themes, interests, tags) */}
          <ClassificationItemsSection
            post={post}
            onDispatch={dispatch}
            CLASSIFICATION_EVENTS={CLASSIFICATION_EVENTS}
            availableGroups={availableGroups}
            availableThemes={availableThemes}
            availableInterests={availableInterests}
            availableTags={availableTags}
            loadingAvailableItems={loadingAvailableItems}
            onAddTagsLocally={addTagsLocally}
            onRefreshTags={refetchAvailableTags}
          />

          <div className="classification-card__footer-group">
            <hr className="classification-card__divider classification-card__divider--user" />

            {/* Footer */}
            <Footer
              updateTimeAgo={post.update_time_ago}
              onDispatch={dispatch}
              CLASSIFICATION_EVENTS={CLASSIFICATION_EVENTS}
              isSpam={isSpam}
            />
          </div>
        </section>
      </article>

      {showClassifyModal && (
        <FullClassifyModal post={post} onClose={() => setShowClassifyModal(false)} />
      )}
    </>
  );
};
